import React from 'react';
import { Input } from 'antd';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import EpiEditor, { LINE_HEIGHT, TEXT_COLUMN } from './EpiEditor';
import SectionBreadcrumb from './SectionBreadcrumb';
import { sectionLabel } from '../utils/epiHelpers';
import { CUSTOM_SECTION_TITLE } from '../types/constants';
import { useEpiContext } from '../context/EpiContext';

const EpiContent: React.FC = () => {
  const {
    lang,
    readOnly,
    selectedSection,
    selectedSectionContent,
    updateSectionTitle,
  } = useEpiContext();

  if (readOnly) {
    return (
      <Content>
        <SectionBreadcrumb />
        <ReadOnlyTitle>
          {sectionLabel(selectedSection?.title ?? '')}
        </ReadOnlyTitle>
        <ReadOnlyBody
          // Section content is TinyMCE HTML; sanitized on the way out the same
          // way the ui-builder's html component does it.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(selectedSectionContent, {
              ADD_ATTR: ['style'],
            }),
          }}
        />
      </Content>
    );
  }

  // A heading the QRD fixes is shown as a heading, not as an input an author
  // has to work out they cannot use. Only titles with something to resolve —
  // `<choose>`, `{fill in}`, or the product-name X — stay editable.
  if (selectedSection?.titleReadonly) {
    return (
      <Content>
        <SectionBreadcrumb />
        <FixedTitle>{selectedSection.title}</FixedTitle>
        <EpiEditor />
      </Content>
    );
  }

  return (
    <Content>
      <SectionBreadcrumb />
      <Input
        key={`${lang}-${selectedSection?.path}`}
        defaultValue={selectedSection?.title ?? ''}
        placeholder={CUSTOM_SECTION_TITLE}
        size="large"
        onChange={e => {
          if (selectedSection) {
            updateSectionTitle(selectedSection.path, e.target.value);
          }
        }}
        style={{ marginBottom: '20px' }}
      />
      <EpiEditor />
    </Content>
  );
};

export default EpiContent;

const Content = styled.div`
  position: sticky;
  top: 90px;
  width: calc(100% - 380px);
  align-self: flex-start;
`;

const FixedTitle = styled.h2`
  /* Size and padding are the h2 defaults. The lighter weight is deliberate —
     the default bold reads as a document heading rather than a field label. */
  margin: 0 0 20px;
  font-weight: 500;
`;

const ReadOnlyTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
`;

const ReadOnlyBody = styled.div`
  /* Same printed text column as the editor — see TEXT_COLUMN in EpiEditor.
     content-box because the admin app resets everything to border-box, which
     would otherwise take the padding below out of the column and wrap a line
     earlier here than in the editor. */
  box-sizing: content-box;
  width: ${TEXT_COLUMN};
  margin: 0 auto;
  font-family: 'Inter', 'Times New Roman', Times, serif;
  font-size: 11pt;
  line-height: ${LINE_HEIGHT};
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  min-height: 200px;

  p {
    margin: 0;
  }

  /* Same reason as the editor: a list item is an ordinary paragraph to the
     export, so the browser's margin would show a blank line that is not
     there. */
  ul,
  ol {
    margin: 0;
  }

  table,
  table th,
  table td {
    border: 1px solid black;
    border-collapse: collapse;
  }

  /* Fixed, as the export lays it out — see the editor. */
  table {
    table-layout: fixed;
  }

  /* The export states tblCellMar at 108 twips on the sides and 0 above and
     below; 108/20 is 5.4pt exactly. See the editor for why the border stays
     at 1px. */
  table th,
  table td {
    padding: 0 5.4pt;
    /* The export emits no w:vAlign and OOXML defaults to top, where a browser
       centres. See the editor. */
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  /* Capped to its column or cell, as the export prints it — see the editor. */
  img {
    max-width: 100%;
    height: auto;
  }

  /* QRD authoring aids, kept visible here so a reviewer can see what the
     printed document will drop. */
  .epi-shaded-text {
    background-color: #d9d9d9;
  }

  .epi-guidance-text {
    color: #008000;
  }

  /* 1.77 x body — see EpiEditor's content_style and epi-html-contract.md. */
  .epi-monitoring-triangle {
    font-size: 1.77em;
    line-height: 1;
  }
`;
